import { useFormik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { IIBSRequestForm, ILOV } from '@interfaces/IBSRequest';
import { useTranslate } from '@translations/useTranslate';
import { GTMEvents } from '@utils/gtm';
import { IFormItemType } from '@interfaces/FormItemType';
import { useGetIBSLookups, useSubmitIBSRequest } from '@services/IBSRequest';
import { Translations } from '@translations/translationsTypes';
import { IBSRequestValidationScheme } from '../IBSRequestValidation.scheme';

export const useGetFormFields = () => {
  const { t, language } = useTranslate();
  const ibsRequestSchema = useMemo(() => IBSRequestValidationScheme(t), [t]);
  const { data: iBSLookupData } = useGetIBSLookups();
  const { mutateAsync: submitIBSRequest } = useSubmitIBSRequest();
  const [error, setError] = useState<string | undefined>(undefined);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleOnSubmit = useCallback(
    async (values: IIBSRequestForm) => {
      try {
        await submitIBSRequest(values);
        GTMEvents.formSubmit('ibs_form', {});
        setIsSuccess(true);
      } catch (ex) {
        setError(t('global.error'));
      }
    },
    [submitIBSRequest, t]
  );

  const ibsOptionsData = useMemo(() => {
    const IBS_BUILDING_TYPE: Array<ILOV> = [];
    const IBS_SERVICE_TYPE: Array<ILOV> = [];
    if (iBSLookupData) {
      if (iBSLookupData) {
        iBSLookupData.IBS_BUILDING_TYPE.forEach((item) => {
          IBS_BUILDING_TYPE.push({
            ...item,
            label: language === Translations.en ? item.value.toString() : item.valueAr,
            value: item.id,
          });
        });
        iBSLookupData.IBS_SERVICE_TYPE.forEach((item) => {
          IBS_SERVICE_TYPE.push({
            ...item,
            label: language === Translations.en ? item.value.toString() : item.valueAr,
            value: item.id,
          });
        });
      }
    }
    return { IBS_BUILDING_TYPE, IBS_SERVICE_TYPE };
  }, [iBSLookupData, language]);

  const formik = useFormik<IIBSRequestForm>({
    initialValues: {
      name: '',
      email: '',
      phoneNumber: '',
      entityName: '',
      entityAddress: '',
      serviceTypeId: '',
      buildingTypeId: '',
      numberOfBuildings: '',
      numberOfUndergroundFloors: '',
      numberOfFloors: '',
      averageFloorArea: '',
      totalUsersDaily: '',
      recaptchaToken: '',
      locationURL: '',
      termsOfUse: false,
      privacyNotice: false,
      cstConditions: false,
    },
    onSubmit: async (values) => {
      await handleOnSubmit(values);
      formik.resetForm();
    },
    validationSchema: ibsRequestSchema,
    validateOnChange: false,
    validateOnBlur: true,
  });

  const userDetailsFormItems: IFormItemType[] = useMemo(() => {
    const formItems: IFormItemType[] = [
      {
        id: 'name',
        label: t('ibs.request.name.label'),
        placeholder: t('ibs.request.name.label'),
        type: 'text',
        required: true,
        value: formik.values.name,
        onChange: (value) => {
          formik.setFieldValue('name', value);
          formik.setFieldError('name', undefined);
        },
        isValid: !formik.errors.name,
        errorFeedback: formik.errors.name,
        isTouched: formik.touched.name,
        maxLength: 100,
      },
      {
        id: 'phoneNumber',
        label: t('ibs.request.phoneNumber.label'),
        placeholder: t('ibs.request.phoneNumber.placeHolder'),
        type: 'text',
        required: true,
        onChange: (value) => {
          formik.setFieldValue('phoneNumber', value);
          formik.setFieldError('phoneNumber', undefined);
        },
        isValid: !formik.errors.phoneNumber,
        errorFeedback: formik.errors.phoneNumber,
        isTouched: formik.touched.phoneNumber,
        value: formik.values.phoneNumber,
      },
      {
        id: 'email',
        label: t('ibs.request.email.label'),
        placeholder: t('ibs.request.email.label'),
        type: 'text',
        required: true,
        onChange: (value) => {
          formik.setFieldValue('email', value);
          formik.setFieldError('email', undefined);
        },
        isValid: !formik.errors.email,
        errorFeedback: formik.errors.email,
        isTouched: formik.touched.email,
        value: formik.values.email,
        maxLength: 150,
      },
      {
        id: 'entityName',
        label: t('ibs.request.entityName.label'),
        placeholder: t('ibs.request.entityName.label'),
        type: 'text',
        required: true,
        onChange: (value) => {
          formik.setFieldValue('entityName', value);
          formik.setFieldError('entityName', undefined);
        },
        isValid: !formik.errors.entityName,
        errorFeedback: formik.errors.entityName,
        isTouched: formik.touched.entityName,
        value: formik.values.entityName,
        maxLength: 150,
      },
      {
        id: 'entityAddress',
        label: t('ibs.request.entityaddress.label'),
        placeholder: t('ibs.request.entityaddress.placeholder'),
        type: 'text',
        required: true,
        disabled: true,
        isValid: !formik.errors.entityAddress,
        errorFeedback: formik.errors.entityAddress,
        isTouched: formik.touched.entityAddress,
        value: formik.values.entityAddress,
        onChange: (value) => {
          formik.setFieldValue('entityAddress', value);
          formik.setFieldError('entityAddress', undefined);
        },
      },
      {
        id: 'serviceTypeId',
        label: t('ibs.request.serviceType.label'),
        placeholder: t('ibs.request.serviceType.placeHolder'),
        type: 'select',
        required: true,
        onChange: (value) => {
          formik.setFieldValue('serviceTypeId', value);
          formik.setFieldError('serviceTypeId', undefined);
        },
        isValid: !formik.errors.serviceTypeId,
        errorFeedback: formik.errors.serviceTypeId,
        isTouched: formik.touched.serviceTypeId,
        value: formik.values.serviceTypeId,
        options: ibsOptionsData.IBS_SERVICE_TYPE,
      },
      {
        id: 'buildingTypeId',
        label: t('ibs.request.buildingType.label'),
        placeholder: t('ibs.request.buildingType.placeholder'),
        type: 'select',
        required: true,
        onChange: (value) => {
          formik.setFieldValue('buildingTypeId', value);
          formik.setFieldError('buildingTypeId', undefined);
        },
        isValid: !formik.errors.buildingTypeId,
        errorFeedback: formik.errors.buildingTypeId,
        isTouched: formik.touched.buildingTypeId,
        value: formik.values.buildingTypeId,
        options: ibsOptionsData.IBS_BUILDING_TYPE,
      },
      {
        id: 'numberOfBuildings',
        label: t('ibs.request.numOfBuildings.label'),
        placeholder: t('ibs.request.numOfBuildings.label'),
        type: 'text',
        required: true,
        onChange: (value) => {
          const regex = /^\d+$/; // Added validation for field which should only accept the numbers this regex will only allow whole numbers
          if (value) {
            if (regex.test(value.toString())) {
              formik.setFieldValue('numberOfBuildings', value);
              formik.setFieldError('numberOfBuildings', undefined);
            }
          } else {
            formik.setFieldValue('numberOfBuildings', value);
            formik.setFieldError('numberOfBuildings', undefined);
          }
        },
        isValid: !formik.errors.numberOfBuildings,
        errorFeedback: formik.errors.numberOfBuildings,
        isTouched: formik.touched.numberOfBuildings,
        value: formik.values.numberOfBuildings,
        maxLength: 3,
      },
      {
        id: 'numberOfFloors',
        label: t('ibs.request.numOfFloors.label'),
        placeholder: t('ibs.request.numOfFloors.label'),
        type: 'text',
        required: false,
        onChange: (value) => {
          const regex = /^\d+$/;
          if (value) {
            if (regex.test(value.toString())) {
              formik.setFieldValue('numberOfFloors', value);
              formik.setFieldError('numberOfFloors', undefined);
            }
          } else {
            formik.setFieldValue('numberOfFloors', value);
            formik.setFieldError('numberOfFloors', undefined);
          }
        },
        value: formik.values.numberOfFloors,
        maxLength: 3,
      },
      {
        id: 'numberOfUndergroundFloors',
        label: t('ibs.request.numOfUgFloors.label'),
        placeholder: t('ibs.request.numOfUgFloors.label'),
        type: 'text',
        required: false,
        onChange: (value) => {
          const regex = /^\d+$/;
          if (value) {
            if (regex.test(value.toString())) {
              formik.setFieldValue('numberOfUndergroundFloors', value);
              formik.setFieldError('numberOfUndergroundFloors', undefined);
            }
          } else {
            formik.setFieldValue('numberOfUndergroundFloors', value);
            formik.setFieldError('numberOfUndergroundFloors', undefined);
          }
        },
        value: formik.values.numberOfUndergroundFloors,
        maxLength: 3,
      },
      {
        id: 'averageFloorArea',
        label: t('ibs.request.avgArea.label'),
        placeholder: t('ibs.request.avgArea.label'),
        type: 'text',
        required: false,
        onChange: (value) => {
          const regex = /^\d+$/;
          if (value) {
            if (regex.test(value.toString())) {
              formik.setFieldValue('averageFloorArea', value);
              formik.setFieldError('averageFloorArea', undefined);
            }
          } else {
            formik.setFieldValue('averageFloorArea', value);
            formik.setFieldError('averageFloorArea', undefined);
          }
        },
        value: formik.values.averageFloorArea,
        maxLength: 10,
      },
      {
        id: 'totalUsersDaily',
        label: t('ibs.request.numOfUsers.label'),
        placeholder: t('ibs.request.numOfUsers.label'),
        type: 'text',
        required: false,
        onChange: (value) => {
          const regex = /^\d+$/;
          if (value) {
            if (regex.test(value.toString())) {
              formik.setFieldValue('totalUsersDaily', value);
              formik.setFieldError('totalUsersDaily', undefined);
            }
          } else {
            formik.setFieldValue('totalUsersDaily', value);
            formik.setFieldError('totalUsersDaily', undefined);
          }
        },
        value: formik.values.totalUsersDaily,
        maxLength: 40,
      },
    ];

    return formItems;
  }, [formik, ibsOptionsData, t]);

  return { formik, userDetailsFormItems, error, isSuccess };
};
